export type AppMode = 'demo' | 'dev' | 'staging' | 'production';
export type ExecutionMode = 'demo' | 'staging' | 'production';

export interface EnvironmentModeInput {
  appMode?: unknown;
  executionMode?: unknown;
  viteMode?: unknown;
  viteProduction?: unknown;
  nodeEnv?: unknown;
}

export interface EnvironmentModeResolution {
  appMode: AppMode;
  executionMode: ExecutionMode;
  expectedExecutionMode: ExecutionMode;
  source: 'explicit' | 'build-default';
  configurationError: string | null;
}

const APP_MODES: readonly AppMode[] = ['demo', 'dev', 'staging', 'production'];

function asNormalizedString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim().toLowerCase()
    : null;
}

export function isValidAppMode(mode: string): mode is AppMode {
  return APP_MODES.includes(mode as AppMode);
}

export function normalizeAppMode(value: unknown): AppMode | null {
  const normalized = asNormalizedString(value);
  return normalized && isValidAppMode(normalized) ? normalized : null;
}

export function normalizeExecutionMode(value: unknown): ExecutionMode | null {
  switch (asNormalizedString(value)) {
    case 'demo':
      return 'demo';
    case 'staging':
    case 'stage':
    case 'test':
    case 'dev':
    case 'development':
      return 'staging';
    case 'production':
    case 'prod':
    case 'live':
      return 'production';
    default:
      return null;
  }
}

export function getExpectedExecutionMode(appMode: AppMode): ExecutionMode {
  if (appMode === 'demo') return 'demo';
  if (appMode === 'production') return 'production';
  return 'staging';
}

/**
 * Resolve product and provider modes together.
 *
 * Local/test execution defaults to Demo for developer convenience. An
 * optimized production build defaults to Production so an omitted variable
 * can never expose sample data on a deployed site. Explicitly configured
 * modes must agree; a mismatch is surfaced as a startup-blocking error.
 */
export function resolveEnvironmentMode(input: EnvironmentModeInput): EnvironmentModeResolution {
  const rawAppMode = asNormalizedString(input.appMode);
  const explicitAppMode = normalizeAppMode(input.appMode);
  const isProductionBuild = input.viteProduction === true
    || asNormalizedString(input.viteMode) === 'production'
    || asNormalizedString(input.nodeEnv) === 'production';
  const appMode = explicitAppMode ?? (isProductionBuild ? 'production' : 'demo');
  const expectedExecutionMode = getExpectedExecutionMode(appMode);
  const rawExecutionMode = asNormalizedString(input.executionMode);
  const explicitExecutionMode = normalizeExecutionMode(input.executionMode);
  const errors: string[] = [];

  if (rawAppMode && !explicitAppMode) {
    errors.push(`Unsupported VITE_APP_MODE "${rawAppMode}".`);
  }

  if (rawExecutionMode && !explicitExecutionMode) {
    errors.push(`Unsupported VITE_EXECUTION_MODE "${rawExecutionMode}".`);
  } else if (explicitExecutionMode && explicitExecutionMode !== expectedExecutionMode) {
    errors.push(
      `VITE_APP_MODE "${appMode}" requires VITE_EXECUTION_MODE "${expectedExecutionMode}", `
      + `but received "${explicitExecutionMode}".`,
    );
  }

  return {
    appMode,
    // Provider selection follows product mode even when configuration is invalid.
    // The application gate prevents startup until the mismatch is fixed.
    executionMode: expectedExecutionMode,
    expectedExecutionMode,
    source: explicitAppMode ? 'explicit' : 'build-default',
    configurationError: errors.length > 0 ? errors.join(' ') : null,
  };
}
